import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Utilitários para limpeza de localStorage quando quota é excedida
 */

import { toast } from '@/hooks/use-toast';

/**
 * Verifica o uso atual do localStorage
 */
export function getStorageUsage(): { used: number; usedMB: string } {
  let totalSize = 0;
  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      totalSize += (localStorage.getItem(key)?.length || 0) * 2; // UTF-16 = 2 bytes per char
    }
  }
  return {
    used: totalSize,
    usedMB: (totalSize / 1024 / 1024).toFixed(2)
  };
}

/**
 * Lista todas as chaves do localStorage com seus tamanhos
 */
export function listStorageKeys(): { key: string; sizeMB: string }[] {
  const keys: { key: string; sizeMB: string }[] = [];
  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const size = (localStorage.getItem(key)?.length || 0) * 2;
      keys.push({
        key,
        sizeMB: (size / 1024 / 1024).toFixed(2)
      });
    }
  }
  return keys.sort((a, b) => parseFloat(b.sizeMB) - parseFloat(a.sizeMB));
}

/**
 * Limpa chaves temporárias e antigas para liberar espaço
 * @returns número de chaves removidas
 */
export function emergencyStorageCleanup(): number {
  const prefixesToClean = [
    'video-features-temp-',
    'video-session-',
    'video-position-',
  ];
  
  let cleaned = 0;
  const currentTempKey = 'video-features-temp-autosave';
  
  try {
    Object.keys(localStorage).forEach(key => {
      // Limpar chaves temporárias (exceto a atual)
      if (prefixesToClean.some(prefix => key.startsWith(prefix)) && key !== currentTempKey) {
        localStorage.removeItem(key);
        cleaned++;
      }
    });
    
    console.log(`🧹 Storage cleanup: removed ${cleaned} temporary keys`);
    return cleaned;
  } catch (error) {
    console.error('Storage cleanup failed:', error);
    return 0;
  }
}

/**
 * Limpa completamente o armazenamento de video features
 * Use apenas em emergência!
 */
export function clearVideoFeaturesStorage(): void {
  try {
    localStorage.removeItem('video-features-storage');
    localStorage.removeItem('video-features-temp-autosave');
    
    // Limpar todas as chaves relacionadas
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('video-features-')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🗑️ video-features-storage cleared completely');
    
    toast({
      title: 'Storage limpo',
      description: translateInterfaceText("Recarregue a página para aplicar as mudanças."),
    });
  } catch (error) {
    console.error('Failed to clear storage:', error);
  }
}

/**
 * Tenta salvar no localStorage com fallback de limpeza automática
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error instanceof DOMException && 
        (error.name === 'QuotaExceededError' || error.code === 22)) {
      console.warn('⚠️ Storage quota exceeded, attempting cleanup...');
      
      // Tentar limpeza de emergência
      const cleaned = emergencyStorageCleanup();
      
      if (cleaned > 0) {
        // Tentar novamente após limpeza
        try {
          localStorage.setItem(key, value);
          console.log('✅ Save successful after cleanup');
          return true;
        } catch (retryError) {
          console.error('❌ Still cannot save after cleanup');
        }
      }
      
      // Notificar usuário
      toast({
        title: 'Armazenamento cheio',
        description: 'Remova algumas imagens ou limpe o cache para continuar.',
        variant: 'destructive',
      });
      
      return false;
    }
    
    throw error;
  }
}
